import ELK from 'elkjs/lib/elk.bundled.js';

// Single shared ELK instance — initialization is heavy (~50ms), so we
// reuse across calls. ELK is stateless after init.
let elkInstance = null;
const getElk = () => {
  if (!elkInstance) elkInstance = new ELK();
  return elkInstance;
};

// Default node dimensions used by ELK when an individual node doesn't
// supply a measured size. Matches the design system: most nodes are
// 240×100 cards (BaseNode w-60 ≈ 240px), annotations 220×120.
const DEFAULT_NODE_SIZE = { width: 240, height: 100 };

const sizeForNode = node => {
  if (node?.type === 'annotation') return { width: 220, height: 120 };
  if (node?.dimensions?.width && node?.dimensions?.height) {
    return { width: node.dimensions.width, height: node.dimensions.height };
  }
  return DEFAULT_NODE_SIZE;
};

// Layered (Sugiyama-style) layout left → right. Most workflow editors
// flow horizontally because reading direction matches L→R for Latin
// scripts. Spacing values are tuned for our 240px node width.
const LAYOUT_OPTIONS = {
  'elk.algorithm': 'layered',
  'elk.direction': 'RIGHT',
  'elk.layered.spacing.nodeNodeBetweenLayers': '80',
  'elk.spacing.nodeNode': '50',
  'elk.layered.spacing.edgeNodeBetweenLayers': '40',
  'elk.layered.nodePlacement.strategy': 'NETWORK_SIMPLEX',
  'elk.layered.crossingMinimization.strategy': 'LAYER_SWEEP',
};

/**
 * Run ELK layout against the current store flow and return a Map of
 * `nodeId → { x, y }` positions.
 *
 * The caller is expected to apply each position via
 * `store.commit('automationFlow/updateNode', { nodeId, updates: { position }, addToHistory })`
 * — we do not mutate the store from here so the caller controls
 * whether the entire layout is one history entry or many.
 *
 * @param {Array} nodes  store-shape nodes ({ id, type, position, data })
 * @param {Array} edges  store-shape connections ({ id, source, target })
 * @returns {Promise<Map<string, {x:number, y:number}>>}
 */
export const computeAutoLayout = async (nodes, edges) => {
  if (!Array.isArray(nodes) || nodes.length === 0) return new Map();

  const elkGraph = {
    id: 'root',
    layoutOptions: LAYOUT_OPTIONS,
    children: nodes.map(node => {
      const size = sizeForNode(node);
      return {
        id: node.id,
        width: size.width,
        height: size.height,
      };
    }),
    edges: (edges || []).map(edge => ({
      id: edge.id,
      sources: [edge.source],
      targets: [edge.target],
    })),
  };

  const elk = getElk();
  const result = await elk.layout(elkGraph);

  const positions = new Map();
  (result.children || []).forEach(child => {
    positions.set(child.id, {
      x: Math.round(child.x ?? 0),
      y: Math.round(child.y ?? 0),
    });
  });
  return positions;
};
